import type { Lesson } from '../types/lesson';
import { isDateInfoFor } from '../utils/lesson';

const API_BASE = 'https://ssblesson.srv595266.hstgr.cloud/api';
// const API_BASE = 'http://45.79.76.14:3000/api';

export interface DateInfo {
  month: number;
  day: number;
  date: string;
  year: number;
  quarter: number;
  lessonNum: number;
  weekday: number;
  thisSaturday: string;
  isFirstSaturday: boolean;
  fsoNum: number;
  next?: DateInfo;
  prev?: DateInfo;
  isOffline?: boolean;
}

// Function to notify about offline/cache usage
const notifyOfflineStatus = () => {
  window.dispatchEvent(new CustomEvent('offlineContent'));
};

export async function fetchDateInfo(year: number, month: number, day: number, signal?: AbortSignal): Promise<DateInfo> {
  const response = await fetch(`${API_BASE}/db/date/${year}/${month}/${day}`, { cache: 'no-store', ...(signal ? { signal } : {}) });
  if (!response.ok) throw new Error(`Failed to fetch date info: ${response.statusText}`);
  const data: unknown = await response.json();
  if (!isDateInfoFor(data, year, month, day)) throw new Error('Invalid date info received');
  const isOffline = response.headers.get('x-offline-fallback') === 'true';
  if (isOffline) notifyOfflineStatus();
  return { ...(data as DateInfo), isOffline };
}

export type LessonFailure = 'unavailable' | 'offline' | 'request';

export class LessonRequestError extends Error {
  constructor(public kind: LessonFailure) {
    super(kind === 'unavailable' ? 'This lesson is not available.' :
      kind === 'offline' ? 'You are offline. Please connect and try again.' :
      'Unable to load this lesson. Please try again.');
    this.name = 'LessonRequestError';
  }
}

export const fetchLesson = async (
  language: string,
  year: number,
  quarter: number,
  lessonNumber: number,
  signal?: AbortSignal
): Promise<Lesson> => {
  try {
    const response = await fetch(
      `${API_BASE}/library/lesson/${language}/${year}/${quarter}/${lessonNumber}`,
      { ...(signal ? { signal } : {}) }
    );
    if (!response.ok) throw new LessonRequestError(response.status === 404 ? 'unavailable' : 'request');
    const data = await response.json();
    if (data?.isOffline) throw new LessonRequestError('offline');
    // This API also reports missing translations as HTTP 200 with an error payload.
    if (typeof data?.error === 'string') throw new LessonRequestError('unavailable');
    if (data?.Year !== year || data?.Quarter !== quarter || data?.Number !== lessonNumber ||
        typeof data?.Title !== 'string' || !Array.isArray(data?.DayLessons)) {
      throw new LessonRequestError('request');
    }
    if (response.headers.get('x-from-cache') === 'true' ||
        response.headers.get('x-offline-fallback') === 'true') notifyOfflineStatus();
    return data;
  } catch (error) {
    if (signal?.aborted || error instanceof LessonRequestError ||
        (error instanceof Error && error.name === 'AbortError')) throw error;
    throw new LessonRequestError(navigator.onLine ? 'request' : 'offline');
  }
};
